import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Globe, Info } from "lucide-react";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { SUPPORTED_LANGS, langMeta, changeLanguageSafe } from "@/i18n";
import globalI18n from "@/i18n";
import { isEnglishPath, toEnglishPath, toSwedishPath } from "@/lib/route-i18n";
import { savePreferredLangToAccount } from "@/lib/lang-preference";

/**
 * Flaggor som SVG-bilder istället för emoji – Windows/Chrome saknar
 * emoji-flaggor och visade tidigare bara landkoden ("SE", "GB").
 */
function Flag({ country, emoji, className = "" }: { country: string | null; emoji: string; className?: string }) {
  if (!country) return <span className={`leading-none ${className}`}>{emoji}</span>;
  return (
    <img
      src={`https://flagcdn.com/${country}.svg`}
      alt=""
      aria-hidden="true"
      loading="lazy"
      className={`inline-block rounded-[2px] object-cover shadow-sm ${className}`}
    />
  );
}

export function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  const onEnglishUrl = isEnglishPath(pathname);
  const current = langMeta(onEnglishUrl ? "en" : i18n.language);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  const change = (code: string) => {
    try { localStorage.setItem("lang", code); } catch {}
    void savePreferredLangToAccount(code);
    setOpen(false);

    // Engelska har egna URL:er (/en/...) som serverrenderas på engelska.
    if (code === "en") {
      if (!onEnglishUrl) {
        const target = toEnglishPath(pathname);
        if (target !== pathname) {
          void router.navigate({ to: target, search: (prev: unknown) => prev as never });
          return;
        }
      }
      globalI18n.changeLanguage("en");
      return;
    }

    void changeLanguageSafe(code);
    if (onEnglishUrl) {
      const target = toSwedishPath(pathname);
      void router.navigate({ to: target, search: (prev: unknown) => prev as never });
    }
  };


  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t("lang.label")}
        className={
          compact
            ? "inline-flex items-center gap-1.5 rounded-md px-2 py-2 text-sm hover:bg-secondary"
            : "inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-2 text-sm hover:bg-secondary"
        }
      >
        <Globe className="h-4 w-4 text-muted-foreground" />
        <Flag country={current.country} emoji={current.flag} className="h-3.5 w-5" />
        <span className="hidden sm:inline text-xs font-medium uppercase">{current.code}</span>
        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
      </button>

      {open && (
        <div className="absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-64 rounded-xl border border-border bg-card shadow-elevated z-50 overflow-hidden">
          <ul className="max-h-80 overflow-y-auto py-1">
            {SUPPORTED_LANGS.map((l) => {
              const active = l.code === (onEnglishUrl ? "en" : i18n.language);
              return (
                <li key={l.code}>
                  <button
                    onClick={() => change(l.code)}
                    className={`flex w-full items-center gap-3 px-3 py-2.5 text-sm hover:bg-secondary ${active ? "bg-secondary/60" : ""}`}
                  >
                    <Flag country={l.country} emoji={l.flag} className="h-4 w-6 shrink-0" />
                    <span className="flex-1 text-start">{l.name}</span>
                    {!l.hasQuestions && (
                      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">UI</span>
                    )}
                    {active && <Check className="h-4 w-4 text-accent" />}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="border-t border-border bg-muted/40 px-3 py-2.5 text-[11px] leading-snug text-muted-foreground">
            <div className="flex items-start gap-1.5">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{t("lang.disclaimer")}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
